import React from 'react';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';
import { formatDateForAPI } from '../../service/subscriptionSchedule';

const FilterSection = ({
  selectedDate,
  setSelectedDate,
  statusFilter,
  setStatusFilter,
  searchTerm,
  setSearchTerm,
  timeSlotFilter,
  setTimeSlotFilter,
  areaFilter,
  setAreaFilter,
  schedules = []
}) => {
  const { t } = useTranslation();

  // Convert 24-hour time to 12-hour format with AM/PM
  const formatTo12Hour = (time24) => {
    if (time24 === null || time24 === undefined) return '';
    
    let hours, minutes = 0;
    
    // Handle different time formats
    if (typeof time24 === 'number') {
      if (time24 <= 23) {
        // Simple hour format: 12, 15, etc.
        hours = time24;
        minutes = 0;
      } else {
        // HHMM format: 1230, 1500, etc.
        hours = Math.floor(time24 / 100);
        minutes = time24 % 100;
      }
    } else {
      // String format
      const timeString = String(time24);
      
      if (timeString.includes(':')) {
        // Format: "14:30" or "14:30:00"
        const timeParts = timeString.split(':');
        hours = parseInt(timeParts[0], 10);
        minutes = parseInt(timeParts[1], 10);
      } else if (timeString.length >= 3) {
        // Format: "1430" (string)
        const timeNum = parseInt(timeString, 10);
        hours = Math.floor(timeNum / 100);
        minutes = timeNum % 100;
      } else {
        // Single or double digit hour as string
        hours = parseInt(timeString, 10);
        minutes = 0;
      }
    }
    
    // Validate hours and minutes
    if (isNaN(hours) || isNaN(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
      return String(time24); // Return original if invalid
    }
    
    const hour12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    const ampm = hours >= 12 ? 'PM' : 'AM';
    
    return `${hour12}:${minutes.toString().padStart(2, '0')} ${ampm}`;
  };

  // Generate unique time slots from schedules
  const getUniqueTimeSlots = () => {
    const timeSlots = schedules
      .map(schedule => ({
        from: schedule.timeToServeFrom,
        to: schedule.timeToServeTo,
        key: `${schedule.timeToServeFrom}-${schedule.timeToServeTo}`,
        displayFrom: formatTo12Hour(schedule.timeToServeFrom),
        displayTo: formatTo12Hour(schedule.timeToServeTo)
      }))
      .filter((slot, index, arr) => 
        arr.findIndex(s => s.key === slot.key) === index
      )
    
    return timeSlots;
  };

  // Generate unique areas from schedules
  const getUniqueAreas = () => {
    const areas = schedules
      .map(schedule => schedule.location?.area)
      .filter(area => area && area.trim() !== '') // Filter out null, undefined, or empty areas
      .filter((area, index, arr) => arr.indexOf(area) === index) // Remove duplicates
      .sort(); // Sort alphabetically
    
    return areas;
  };

  const timeSlots = getUniqueTimeSlots();
  const areas = getUniqueAreas();

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('date', 'Date')}
          </label>
          <input
            type="date"
            value={formatDateForAPI(selectedDate)}
            onChange={(e) => setSelectedDate(new Date(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('status.title')}
          </label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="ALL">{t('all', 'All')}</option>
            <option value="SCHEDULED">{t('status.scheduled', 'Scheduled')}</option>
            <option value="PENDING">{t('status.pending', 'Pending')}</option>
            <option value="READY_TO_DELIVER">{t('status.readyToDeliver', 'Ready to Deliver')}</option>
            <option value="IN_DELIVERY">{t('status.inDelivery', 'In Delivery')}</option>
            <option value="DELIVERED">{t('status.delivered', 'Delivered')}</option>
            <option value="MISSED">{t('status.missed', 'Missed')}</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('timeSlot', 'Time Slot')}
          </label>
          <select
            value={timeSlotFilter}
            onChange={(e) => setTimeSlotFilter(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="ALL">{t('allTimeSlots', 'All Time Slots')}</option>
            {timeSlots.map((slot) => (
              <option key={slot.key} value={slot.key}>
                {slot.displayFrom} - {slot.displayTo}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('area', 'Area')}
          </label>
          <select
            value={areaFilter}
            onChange={(e) => setAreaFilter(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="ALL">{t('allAreas', 'All Areas')}</option>
            {areas.map((area) => (
              <option key={area} value={area}>
                {area}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('search', 'Search')}
          </label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder={t('searchPlaceholder', 'Search meals or subscriptions...')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilterSection;
